// 9fbef606107a605d69c0edbcd8029e5d
import React from 'react';
import PropTypes from 'prop-types';
import withStyles from '@tcp/core/src/components/common/hoc/withStyles';
import Row from '@tcp/core/src/components/common/atoms/Row';
import { getLocator } from '@tcp/core/src/utils/utils';
import BodyCopy from '@tcp/core/src/components/common/atoms/BodyCopy';
import Col from '@tcp/core/src/components/common/atoms/Col';
import StoreStaticMap from '@tcp/core/src/components/common/atoms/StoreStaticMap';
import Anchor from '@tcp/core/src/components/common/atoms/Anchor';
import RichText from '@tcp/core/src/components/common/atoms/RichText';
import StoreAddressTile from '@tcp/core/src/components/common/molecules/StoreAddressTile';
import StoreHours from '@tcp/core/src/components/common/molecules/StoreHours';
import StoreClosedMessage from '@tcp/core/src/components/common/molecules/StoreHours/views/StoreClosedMessage';
import StoreLocations from '@tcp/core/src/components/common/molecules/StoreLocations';
import { getViewportInfo, isCanada, getAPIConfig, isClient } from '@tcp/core/src/utils';
import {
  GlobalActionsLabels,
  GLOBAL_ACTIONS_LABELS,
  lblBack,
} from '@tcp/core/src/utils/labels/globalActions.labels';
import SeoCopy from '@tcp/core/src/components/features/browse/ProductListing/molecules/SeoCopy/views';
import formatStoreTiming from '../../../../../../utils/formatStoreTiming';
import StoreStaticMapGmaps from '../../../../../common/atoms/StoreStaticMap/views/StoreStaticMapGmaps';
import OthersStoresSkelton from '../../skelton/OtherStoresSkelton.view';
import style from '../styles/StoreDetail.style';
import {
  getLabelFromCopyOrFromTranslation,
  renderLabel,
} from '../../../../../../utils/labels/labelUtilty';
import {
  STORE_DETAILS_LABELS,
  STORE_LANDING_LABELS,
  StoreLocatorLabels,
  lblStoredetailsEntranceType,
  lblStoredetailsMallType,
} from '../../../Labels/StoreLocator.labels';
import StoreSpecialCarousel from '../../../../browse/ProductDetail/molecules/StoreSpecialCarousel';
import StoreSpecialBanner from '../../../../browse/ProductDetail/molecules/StoreSpecialBanner';
import StoreDetailLocalSchema from '../../localSchema/StoreDetail.localSchema';

export const createRichText = (richTextContent) => <RichText richTextHtml={richTextContent} />;

export const getStoreStaticMap = (store, mapboxSwitch, isStoreDetailsRedesignEnabled) => {
  let StoreStaticMapCommon = StoreStaticMap;
  let apiKey = getAPIConfig().mapBoxApiKey;
  if (!mapboxSwitch) {
    StoreStaticMapCommon = StoreStaticMapGmaps;
    apiKey = getAPIConfig().googleApiKey;
  }
  return (
    <StoreStaticMapCommon
      storesList={[store]}
      centeredStoreId={store.basicInfo && store.basicInfo.id}
      isMobile={isClient() && getViewportInfo().isMobile}
      isCanada={isCanada}
      apiKey={apiKey}
      isStoreDetailsRedesignEnabled={isStoreDetailsRedesignEnabled}
    />
  );
};

const getStoreTimings = (hours) => {
  const storeTimings = [];
  if (hours !== undefined && Object.keys(hours).length > 0)
    Object.keys(hours).forEach((hour) => storeTimings.push(...hours[hour]));

  return storeTimings;
};

const renderBackBtn = (oldDesign, routesBack, className) => {
  return (
    oldDesign && (
      <Anchor
        fontSizeVariation="xlarge"
        anchorVariation="secondary"
        handleLinkClick={routesBack}
        noLink
        className={`${className}__backlink back-anchor`}
        title={renderLabel(
          getLabelFromCopyOrFromTranslation(
            `${GLOBAL_ACTIONS_LABELS}.${lblBack}`,
            GlobalActionsLabels
          ),
          'lbl_back'
        )}
      >
        <span className="left-arrow" />
        {renderLabel(
          getLabelFromCopyOrFromTranslation(
            `${GLOBAL_ACTIONS_LABELS}.${lblBack}`,
            GlobalActionsLabels
          ),
          'lbl_back'
        )}
      </Anchor>
    )
  );
};

const isStoreDetails = (store) => store?.basicInfo !== undefined;

const checkToShowStoreSpecialBanner = (storeMessage, defaultStoreMessage) => {
  const currentDate = new Date();

  const isWithinDateRange = (startDate, endDate) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    return currentDate >= start && currentDate <= end;
  };

  const getValidMessage = (messages) => {
    if (messages && Array.isArray(messages) && messages?.length > 0) {
      const { StartDate, EndDate } = messages[0];
      if (isWithinDateRange(StartDate, EndDate)) {
        return messages[0];
      }
    }
    return null;
  };

  return getValidMessage(storeMessage) || getValidMessage(defaultStoreMessage);
};

const renderStoreSpecialBanner = ({ isMobile, className, shouldShowStoreSpecialBanner }) => {
  if (isMobile) {
    return (
      shouldShowStoreSpecialBanner && (
        <Col colSize={{ small: 6, medium: 8, large: 12 }} className="storeSpecialBannerWrapper">
          <StoreSpecialBanner
            className={className}
            shouldShowStoreSpecialBanner={shouldShowStoreSpecialBanner}
          />
        </Col>
      )
    );
  }
  return (
    shouldShowStoreSpecialBanner && (
      <Col colSize={{ small: 6, medium: 8, large: 12 }} className="storeSpecialBannerWrapper">
        <StoreSpecialBanner
          className={className}
          shouldShowStoreSpecialBanner={shouldShowStoreSpecialBanner}
        />
      </Col>
    )
  );
};

// eslint-disable-next-line complexity
const StoreDetail = ({
  className,
  store,
  labels,
  otherStores,
  openStoreDetails,
  openStoreDirections,
  routesBack,
  setFavoriteStore,
  isFavorite,
  fetchRichContent,
  seoData,
  mapboxSwitch,
  isStoreHoursWebEnabled,
  currentStoreHolidayHours,
  specialHoursNumberOfDaysCount,
  isStoreDetailsDesktopRedesignEnabled,
  isStoreDetailsRedesignMobileEnabled,
  setDefaultSuggestedStoresAction,
  isBossMultiStoreEnabled,
  isShopThisStoreEnabled,
  isStsCtaEnabled,
  updateStore,
  isStoreDetailsRedesignEnabled,
  oldDesign,
  isNearByStoresLoading,
  onQuickViewOpenClick,
  promotionsData,
  serverTime,
  isBuyMorePromoEnabled,
  isTileRedesignAB,
  isUpdatedPromotionsDisplayEnabled,
  isStoreSpecialProductsOnStoreDetails,
  isStoreSpecialsPriceOn,
  storeSpecialDiscount,
  fetchStoreSpecialProducts,
  storeSpecialProducts,
  storeDiscount,
  defaultStoreMessage,
  isStoreSpecialProductsLoading,
  openViewMapModal,
  isViewMapEnabled,
  // eslint-disable-next-line sonarjs/cognitive-complexity
}) => {
  const { isMobile } = (isClient() && getViewportInfo()) || {};
  const {
    lbl_storedetails_mallType: storeDetailsMallType,
    lbl_storedetails_entranceType: storeDetailsEntranceType,
  } = getLabelFromCopyOrFromTranslation(STORE_DETAILS_LABELS, StoreLocatorLabels);
  const { hours, features, storeMessage } = store;

  const { lbl_storelanding_findStoreHeading: lblStorelandingFindStoreHeading } =
    getLabelFromCopyOrFromTranslation(STORE_LANDING_LABELS, StoreLocatorLabels);
  const storeMeta = [
    {
      label: renderLabel(storeDetailsMallType, lblStoredetailsMallType),
      value: features !== undefined && features.mallType,
    },
    {
      label: renderLabel(storeDetailsEntranceType, lblStoredetailsEntranceType),
      value: features !== undefined && features.entranceType,
    },
  ];
  const storeTimings = getStoreTimings(hours);

  const isStoreDetailsAvailable = isStoreDetails(store);

  const isStorePeramanentClosed = storeTimings[0]?.isClosed;
  const shouldShowStoreSpecialBanner = checkToShowStoreSpecialBanner(
    storeMessage,
    defaultStoreMessage
  );

  const renderOtherStores = () => {
    return isNearByStoresLoading && !currentStoreHolidayHours ? (
      <OthersStoresSkelton />
    ) : (
      <StoreLocations
        className="storedetail__storelocation"
        stores={otherStores}
        labels={labels}
        openStoreDetails={openStoreDetails}
        type="storelocations"
        isStoreDetailsDesktopRedesignEnabled={isStoreDetailsDesktopRedesignEnabled}
        isStoreDetailsRedesignMobileEnabled={isStoreDetailsRedesignMobileEnabled}
        isStsCtaEnabled={isStsCtaEnabled}
        isShopThisStoreEnabled={isShopThisStoreEnabled}
        updateStore={updateStore}
        fromStoreDetailPage
      />
    );
  };

  if (isStoreDetailsDesktopRedesignEnabled) {
    return isStoreDetailsAvailable ? (
      <>
        {!isMobile && <StoreDetailLocalSchema store={store} storeTimings={storeTimings} />}
        <div className={className}>
          <div>
            <Row>
              {renderStoreSpecialBanner({ className, shouldShowStoreSpecialBanner })}
              <Col colSize={{ small: 6, medium: 4, large: 4 }}>
                <Anchor
                  fontSizeVariation="medium"
                  underline
                  anchorVariation="primary"
                  to="/store-locator"
                >
                  <BodyCopy
                    component="div"
                    data-locator={getLocator('store_selected_storelabel')}
                    fontFamily="secondary"
                    fontSize="fs13"
                    className="selected-store-lbl"
                  >
                    {renderLabel(
                      lblStorelandingFindStoreHeading,
                      'lbl_storelanding_findStoreHeading'
                    )}
                  </BodyCopy>
                </Anchor>
                <StoreAddressTile
                  className="storeinfo-new"
                  variation="detail"
                  store={store}
                  labels={labels}
                  openStoreDirections={openStoreDirections}
                  setFavoriteStore={setFavoriteStore}
                  isFavorite={isFavorite}
                  showSetFavorite
                  tileButtonClass="storeaddressinfo__btn"
                  isStoreHoursWebEnabled={isStoreHoursWebEnabled}
                  isStoreDetailsDesktopRedesignEnabled={isStoreDetailsDesktopRedesignEnabled}
                  setDefaultSuggestedStoresAction={setDefaultSuggestedStoresAction}
                  isBossMultiStoreEnabled={isBossMultiStoreEnabled}
                  isStsCtaEnabled={isStsCtaEnabled}
                  isShopThisStoreEnabled={isShopThisStoreEnabled}
                  updateStore={updateStore}
                  fromStoreDetailPage
                  openViewMapModal={openViewMapModal}
                  isViewMapEnabled={isViewMapEnabled}
                />
                {isStorePeramanentClosed !== true && (
                  <StoreHours
                    title="Store Hours"
                    defaultOpen
                    storeTiming={formatStoreTiming(storeTimings)}
                    storeMeta={storeMeta}
                    isAccordionTablet
                    labels={labels}
                    isStoreHoursWebEnabled={isStoreHoursWebEnabled}
                    specialHoursNumberOfDaysCount={specialHoursNumberOfDaysCount}
                    currentStoreHolidayHours={currentStoreHolidayHours}
                    formattedCurrentStoreHolidayHours={formatStoreTiming(currentStoreHolidayHours)}
                    isStoreDetailsDesktopRedesignEnabled={isStoreDetailsDesktopRedesignEnabled}
                  />
                )}
                {isStorePeramanentClosed && <StoreClosedMessage />}
              </Col>
              <Col colSize={{ small: 6, medium: 4, large: 8 }}>
                <div className="hide-on-mobile">
                  <StoreSpecialCarousel
                    onQuickViewOpenClick={onQuickViewOpenClick}
                    promotionsData={promotionsData}
                    serverTime={serverTime}
                    isBuyMorePromoEnabled={isBuyMorePromoEnabled}
                    isTileRedesignAB={isTileRedesignAB}
                    isUpdatedPromotionsDisplayEnabled={isUpdatedPromotionsDisplayEnabled}
                    isStoreSpecialProductsOnStoreDetails={isStoreSpecialProductsOnStoreDetails}
                    isStoreSpecialsPriceOn={isStoreSpecialsPriceOn}
                    storeSpecialDiscount={storeSpecialDiscount}
                    fetchStoreSpecialProducts={fetchStoreSpecialProducts}
                    store={store}
                    storeSpecialProducts={storeSpecialProducts}
                    storeDiscount={storeDiscount}
                    setFavoriteStore={setFavoriteStore}
                    isStoreSpecialProductsLoading={isStoreSpecialProductsLoading}
                  />
                </div>
                {getStoreStaticMap(store, mapboxSwitch, isStoreDetailsRedesignEnabled)}
                {createRichText(fetchRichContent('storelocator_Promo1'))}
                {createRichText(fetchRichContent('storelocator_Promo2'))}
                {createRichText(fetchRichContent('storelocator_Promo3'))}
                {createRichText(fetchRichContent('storeLocator_description'))}
                {renderOtherStores()}
                <div className="seo-data-dsk-view hide-on-mobile hide-on-tablet">
                  <SeoCopy {...seoData} />
                </div>
              </Col>
            </Row>
          </div>
          <Row className={'seo-data-tab-view hide-on-mobile'}>
            <SeoCopy {...seoData} />
          </Row>
        </div>
      </>
    ) : null;
  }
  if (isStoreDetailsRedesignMobileEnabled || oldDesign) {
    return isStoreDetailsAvailable ? (
      <>
        {isMobile && <StoreDetailLocalSchema store={store} storeTimings={storeTimings} />}
        <div className={className}>
          {renderBackBtn(oldDesign, routesBack, className)}
          <Row className={isStoreDetailsRedesignMobileEnabled && 'store-hrs-mobileview'}>
            {renderStoreSpecialBanner({ isMobile, className, shouldShowStoreSpecialBanner })}

            <Col colSize={{ small: 6, medium: 4, large: 4 }}>
              <StoreAddressTile
                className="storeinfo"
                variation="detail"
                store={store}
                labels={labels}
                openStoreDirections={openStoreDirections}
                setFavoriteStore={setFavoriteStore}
                isFavorite={isFavorite}
                showSetFavorite
                tileButtonClass="storeaddressinfo__btn"
                isStoreHoursWebEnabled={isStoreHoursWebEnabled}
                isStoreDetailsRedesignMobileEnabled={isStoreDetailsRedesignMobileEnabled}
                setDefaultSuggestedStoresAction={setDefaultSuggestedStoresAction}
                isBossMultiStoreEnabled={isBossMultiStoreEnabled}
                isStsCtaEnabled={isStsCtaEnabled}
                isShopThisStoreEnabled={isShopThisStoreEnabled}
                updateStore={updateStore}
                fromStoreDetailPage
                openViewMapModal={openViewMapModal}
                isViewMapEnabled={isViewMapEnabled}
              />
              {isStorePeramanentClosed !== true && (
                <StoreHours
                  title="Store Hours"
                  defaultOpen
                  storeTiming={formatStoreTiming(storeTimings)}
                  storeMeta={storeMeta}
                  isAccordionTablet
                  labels={labels}
                  isStoreHoursWebEnabled={isStoreHoursWebEnabled}
                  specialHoursNumberOfDaysCount={specialHoursNumberOfDaysCount}
                  currentStoreHolidayHours={currentStoreHolidayHours}
                  formattedCurrentStoreHolidayHours={formatStoreTiming(currentStoreHolidayHours)}
                  isStoreDetailsRedesignMobileEnabled={isStoreDetailsRedesignMobileEnabled}
                  isStoreDetailsDesktopRedesignEnabled={isStoreDetailsDesktopRedesignEnabled}
                />
              )}
              {isStorePeramanentClosed && <StoreClosedMessage />}
            </Col>
            <Col
              className={isStoreDetailsRedesignMobileEnabled && 'google-map-wrapper'}
              colSize={{ small: 6, medium: 4, large: 8 }}
            >
              <div className="hide-on-desktop hide-on-tablet">
                <StoreSpecialCarousel
                  onQuickViewOpenClick={onQuickViewOpenClick}
                  promotionsData={promotionsData}
                  serverTime={serverTime}
                  isBuyMorePromoEnabled={isBuyMorePromoEnabled}
                  isTileRedesignAB={isTileRedesignAB}
                  isUpdatedPromotionsDisplayEnabled={isUpdatedPromotionsDisplayEnabled}
                  isStoreSpecialProductsOnStoreDetails={isStoreSpecialProductsOnStoreDetails}
                  isStoreSpecialsPriceOn={isStoreSpecialsPriceOn}
                  storeSpecialDiscount={storeSpecialDiscount}
                  fetchStoreSpecialProducts={fetchStoreSpecialProducts}
                  store={store}
                  storeSpecialProducts={storeSpecialProducts}
                  storeDiscount={storeDiscount}
                  setFavoriteStore={setFavoriteStore}
                  isStoreSpecialProductsLoading={isStoreSpecialProductsLoading}
                />
              </div>

              {getStoreStaticMap(store, mapboxSwitch, isStoreDetailsRedesignEnabled)}
              {createRichText(fetchRichContent('storelocator_Promo1'))}
            </Col>
          </Row>
          <Row>
            <Col colSize={{ small: 6, medium: 8, large: 12 }}>{renderOtherStores()}</Col>
          </Row>
          <Row>
            <Col colSize={{ small: 6, medium: 4, large: 6 }}>
              {createRichText(fetchRichContent('storelocator_Promo2'))}
            </Col>
            <Col colSize={{ small: 6, medium: 4, large: 6 }}>
              {createRichText(fetchRichContent('storelocator_Promo3'))}
            </Col>
          </Row>
          <Row>
            <Col colSize={{ small: 6, medium: 8, large: 12 }}>
              {createRichText(fetchRichContent('storeLocator_description'))}
            </Col>
          </Row>
          <div className="hide-on-desktop hide-on-tablet">
            <SeoCopy
              {...seoData}
              isStoreDetailsRedesignMobileEnabled={isStoreDetailsRedesignMobileEnabled}
            />
          </div>
        </div>
      </>
    ) : null;
  }
  return null;
};

StoreDetail.propTypes = {
  className: PropTypes.string.isRequired,
  store: PropTypes.shape({
    basicInfo: PropTypes.shape({
      id: PropTypes.string,
      storeName: PropTypes.string,
      phone: PropTypes.string,
      address: PropTypes.shape({
        addressLine1: PropTypes.string,
        city: PropTypes.string,
        state: PropTypes.string,
        country: PropTypes.string,
        zipCode: PropTypes.string,
      }),
      coordinates: PropTypes.shape({
        lat: PropTypes.number,
        long: PropTypes.number,
      }),
    }),
    hours: PropTypes.shape({}),
    features: PropTypes.shape({
      mallType: PropTypes.string,
      entranceType: PropTypes.string,
    }),
  }).isRequired,
  labels: PropTypes.shape({}).isRequired,
  otherStores: PropTypes.shape([]).isRequired,
  openStoreDetails: PropTypes.func.isRequired,
  openStoreDirections: PropTypes.func.isRequired,
  routesBack: PropTypes.func.isRequired,
  setFavoriteStore: PropTypes.func.isRequired,
  isFavorite: PropTypes.bool.isRequired,
  fetchRichContent: PropTypes.func.isRequired,
  seoData: PropTypes.shape({}).isRequired,
  mapboxSwitch: PropTypes.bool.isRequired,
  setDefaultSuggestedStoresAction: PropTypes.func.isRequired,
  isBossMultiStoreEnabled: PropTypes.bool.isRequired,
};

export default withStyles(StoreDetail, style);

export { StoreDetail as StoreDetailVanilla };
